import { Page, Header, HeaderLabel, Content } from '@backstage/core-components';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForwardIcon } from '../icons/ArrowForwardIcon';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type PlatformCardProps = {
  name: string;
  description: string;
  to: string;
  external?: boolean;
};

const PlatformCard = ({
  name,
  description,
  to,
  external,
}: PlatformCardProps) => {
  const isExternal = external ?? to.startsWith('http');

  const footerContent = (
    <>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          mr: 0.5,
        }}
      >
        READ MORE
      </Typography>
      {isExternal ? (
        <OpenInNewIcon sx={{ width: 16, height: 16 }} />
      ) : (
        <Box sx={{ width: 16, height: 16, display: 'flex' }}>
          <ArrowForwardIcon />
        </Box>
      )}
    </>
  );

  return (
    <Card
      sx={{
        borderRadius: 1,
        boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.20)',
        display: 'grid',
        gridTemplateRows: 'subgrid',
        gridRow: 'span 4',
      }}
    >
      <Box
        sx={{
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          background: 'linear-gradient(90deg, #008294 0%, #66B8C4 100%)',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.5,
            fontFamily: '"Helvetica Neue", Roboto, sans-serif',
            color: '#FFFFFF',
            margin: 0,
          }}
        >
          {name}
        </Typography>
      </Box>

      <CardContent sx={{ padding: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </CardContent>

      <Divider />

      {isExternal ? (
        <Box
          component="a"
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            padding: '12px 24px 14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {footerContent}
        </Box>
      ) : (
        <Box
          component={RouterLink}
          to={to}
          sx={{
            padding: '12px 24px 14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {footerContent}
        </Box>
      )}
    </Card>
  );
};

export const ToolingPage = () => {
  return (
    <Page themeId="plugin">
      <Header
        title="Developer Tooling"
        subtitle="Major software platforms and toolchains available in the Traton Developer Portal."
      >
        <HeaderLabel
          label="Owner"
          value="Core Portal"
          url="/catalog/default/group/core-portal"
        />
      </Header>

      <Content stretch noPadding>
        <Box
          sx={{
            minWidth: '100%',
            minHeight: '100vh',
            boxSizing: 'border-box',
            bgcolor: theme =>
              theme.palette.mode === 'light' ? '#F9F7F2' : 'background.default',
            p: 4,
            pb: 5,
          }}
        >
          <Box
            sx={{
              width: 1368,
              maxWidth: '100%',
            }}
          >
            <Box sx={{ maxWidth: 640, mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                Welcome to the Traton software development landscape!
              </Typography>

              <Typography variant="body1" paragraph>
                Here you will find the major platforms and toolchains in the
                Traton software development landscape. They make it possible for
                you to create anything from cloud services to vehicle functions
                and automated AI workflows. If you miss a platform or toolchain,
                contact us.
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                mt: 4,
                mb: 2,
              }}
            >
              Major platforms and toolchains for software development
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
                gridAutoRows: 'auto auto 1fr auto',
              }}
            >
              <PlatformCard
                name="CHIPPS"
                description="CHIPPS is the platform for High-Performance ECUs (HPCs), which separates hardware from applications and enables application developers to integrate, build, test and deploy with high speed for all brands. It offers common processes, methods and tools."
                to="/docs/default/component/traton-one-os-chipps-golden-path"
                external={false}
              />
              <PlatformCard
                name="CRISP"
                description="CRISP is the Common Real-time Software Platform supporting real-time processes for i.e. braking, engine management and safety systems in the vehicle ECUs. It enhances real-time performance and increase portability by providing multiple abstraction layers."
                to="https://confluence.collaborationlayer-traton.com/spaces/CRCP/pages/145330669/What+is+CRiSP"
                external
              />
              <PlatformCard
                name="Developer Platform"
                description="Build, deploy and operate software with ease. Developer Platform connects your essential tools and services, bringing you documentation, pipelines, repos, cloud, observability, access management and more. Focus on creating value instead of managing setup and infrastructure."
                to="/tooling/developer-platform"
                external={false}
              />
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
